import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import { Alert } from './Alert.js';
import { Button } from './Button.js';
import { Card } from './Card.js';
import {
  cartesianRows,
  colorVar,
  formatNumber,
  formatShare,
  itemShares,
  pieSlices,
  raceColors,
  raceFrames,
  seriesColors,
  type ChartColor,
  type SeriesSpec,
  type Slice,
} from './chart.js';
import { cn } from './cn.js';
import { Filters, type FilterFieldSlot, type FilterValues } from './Filters.js';
import { Icon } from './Icon.js';
import { ChartContainer, ChartTooltip } from './primitives/chart.js';
import { Table, type Column } from './Table.js';

/**
 * O gráfico da casa — um componente, seis formas, e a forma do dado é uma só.
 *
 * | `kind`          | para que                                                 |
 * | --------------- | -------------------------------------------------------- |
 * | `linha`         | o que muda ao longo do tempo                             |
 * | `barra`         | comparar tamanhos entre categorias                       |
 * | `barra-linha`   | as duas na mesma figura, **na mesma unidade**            |
 * | `pizza`         | a parte de um todo, com até seis fatias (rosca: `center`) |
 * | `pizza-tabela`  | a pizza, e a `Table` ao lado com cada registro           |
 * | `corrida`       | o ranking quadro a quadro (bar chart race)               |
 *
 * ## O que ele não faz, de propósito
 *
 * - **Não busca nem filtra dado.** O `Filters` mora no cabeçalho, e o
 *   `onApply` volta para a tela, que refaz a consulta. Filtrar no navegador
 *   seria filtrar só a página que chegou, e o gráfico desenharia a amostra
 *   como se fosse o todo.
 * - **Não tem dois eixos verticais.** Barra e linha dividem o mesmo eixo, e
 *   portanto a mesma unidade. Dois eixos fazem o cruzamento das séries ser
 *   um acidente de escala — o mesmo motivo que o `TargetTrend` do comercial
 *   registra. Duas unidades são dois gráficos.
 * - **Não dá cor em ciclo.** São seis séries no máximo; a sétima quebra
 *   (`seriesColor`), e a pizza junta as menores num "Outros" cinza.
 * - **Não formata dinheiro por conta própria.** `format` é de quem chama: o
 *   valor chega como `Money` em centavos, e a tela passa o formatador dela.
 *
 * ## A leitura exata mora na dica e na tabela
 *
 * Número em cada ponto transforma a figura numa tabela mal diagramada. O
 * número aparece ao passar o ponteiro (`tooltip` troca o conteúdo da dica pelo
 * cartão de relatório de quem chama), e o botão **Ver tabela** troca a figura
 * pelos mesmos números em `Table` — que é também o caminho de quem usa leitor
 * de tela ou não distingue as cores.
 *
 * Mora em `@evogard/ui/chart`, e não no barril do pacote, para o Recharts só
 * ser baixado pela tela que desenha gráfico.
 */
export type ChartProps<T> = ChartCommon<T> &
  (LineOrBarProps<T> | ComboProps<T> | PieProps<T> | RaceProps<T>);

interface ChartCommon<T> {
  title: ReactNode;
  descricao?: ReactNode;
  /** O que a figura mostra, numa frase — o nome dela para o leitor de tela, e da tabela. */
  label: string;
  data: readonly T[];
  /** Ensina a tela: o que apareceria aqui, e não que não há nada. */
  empty: string;
  loading?: boolean;
  /** A consulta falhou. O gráfico não desenha o que tinha antes como se fosse atual. */
  error?: Error | null;
  /** O painel de recortes da casa, no cabeçalho. Quem aplica é a tela. */
  filters?: {
    fields: FilterFieldSlot[];
    values: FilterValues;
    onApply: (values: FilterValues) => void;
  };
  /** O número como texto — no eixo, na dica e na tabela. Padrão: inteiro à brasileira. */
  format?: (value: number) => string;
  /**
   * O número do EIXO vertical, quando `format` não cabe nos 64px dele — dinheiro
   * com centavo, por exemplo. A dica e a tabela continuam com `format`, que é o
   * número exato. Padrão: `format`.
   */
  axisFormat?: (value: number) => string;
  /** Altura da área de desenho, em px. */
  height?: number;
  /** Ações extras do cabeçalho, à esquerda dos filtros. */
  actions?: ReactNode;
  /**
   * Tira o botão **Ver tabela**. Só para o gráfico que já tem os mesmos números
   * em lista na tela — como o dia a dia do relatório da premiação, com a lista
   * de boletos logo abaixo (28/10/2026, pedido do dono). Sem a lista ao lado,
   * o botão é o caminho de quem usa leitor de tela, e fica.
   */
  withoutTable?: boolean;
  /**
   * Sem a casca do `Card` — para o gráfico que mora DENTRO de um bloco que já
   * é cartão, com os números ao lado (o de Boletos das Métricas, 29/10/2026).
   * Cartão dentro de cartão está sempre errado. O `title` continua sendo o
   * nome do grupo para o leitor de tela, junto do `label`.
   */
  bare?: boolean;
}

/** O que a dica recebe ao passar o ponteiro numa categoria. */
export interface ChartPoint<T> {
  category: string;
  item: T;
  values: { key: string; label: string; value: number | null; color: ChartColor }[];
}

interface LineOrBarProps<T> {
  kind: 'linha' | 'barra';
  category: (item: T) => string;
  series: SeriesSpec<T>[];
  /** Barras empilhadas. Só faz sentido quando as séries são partes do mesmo todo. */
  stacked?: boolean;
  /**
   * Só em `linha`: pinta a área embaixo da linha, num degradê da cor da série
   * que some no eixo — a Evolução das Métricas (29/10/2026). O ponto some
   * junto: na área, a forma é o que se lê, e o número mora na dica.
   */
  area?: boolean;
  /** O cartão de relatório da dica. Sem ele, a dica lista as séries. */
  tooltip?: (point: ChartPoint<T>) => ReactNode;
  /**
   * O clique numa categoria — o vencimento que filtra os KPIs e a tabela do
   * relatório da premiação (28/10/2026, pedido do dono). Quem guarda a escolha
   * é a tela; o gráfico só avisa e, com `selected`, apaga as outras colunas.
   * O clique é atalho: a tela que o usa precisa mostrar a escolha e um jeito
   * de desfazê-la fora do gráfico, porque coluna não recebe foco de teclado.
   */
  onSelect?: (item: T) => void;
  /** A categoria escolhida — as outras colunas ficam apagadas. */
  selected?: (item: T) => boolean;
}

interface ComboProps<T> {
  kind: 'barra-linha';
  category: (item: T) => string;
  /** Cada série diz se é barra ou linha — não há um padrão que adivinhe. */
  series: (SeriesSpec<T> & { mark: 'barra' | 'linha' })[];
  stacked?: boolean;
  tooltip?: (point: ChartPoint<T>) => ReactNode;
}

interface PieProps<T> {
  kind: 'pizza' | 'pizza-tabela';
  slice: {
    label: (item: T) => string;
    value: (item: T) => number;
    /**
     * A cor de ESTADO da fatia, quando a cor é o significado — "entra" em verde
     * e "deixa de entrar" em vermelho. Sem ela, a distribuição automática.
     */
    color?: (item: T) => ChartColor | undefined;
  };
  /** Quantas fatias com cor própria antes do "Outros". De 2 a 6. */
  maxSlices?: number;
  /** Colunas extras da tabela, depois de Fatia, Valor e Participação. */
  columns?: Column<T>[];
  tooltip?: (slice: Slice<T>) => ReactNode;
  /**
   * O miolo da ROSCA: com ele a pizza ganha o furo, e o que vier aqui fica no
   * centro — o total curto e o que ele mede, como os cortes do Painel do
   * Financeiro (29/10/2026). Quem formata é a tela: o furo tem uns 120px, e o
   * `format` exato com centavo não cabe nele.
   */
  center?: ReactNode;
  /**
   * O clique numa fatia — a pizza que abre outra, como a da Previsão da
   * Carteira (29/10/2026, pedido do dono). Quem guarda a escolha é a tela. O
   * nome da fatia na tabela vira botão junto, porque setor de SVG não recebe
   * foco de teclado. O "Outros" não abre: ele não é um registro.
   */
  onSelect?: (item: T) => void;
}

interface RaceProps<T> {
  kind: 'corrida';
  /** O quadro (o período). Os dados chegam **em ordem**: a primeira aparição é a posição. */
  frame: (item: T) => string;
  entity: (item: T) => string;
  value: (item: T) => number;
  /** Quantas barras por quadro. */
  top?: number;
  /** Tempo de cada quadro ao tocar, em ms. */
  interval?: number;
}

const DEFAULT_HEIGHT = 280;

export function Chart<T>(props: ChartProps<T>) {
  const { title, descricao, label, data, empty, loading, error, filters, actions } =
    props;
  const format = props.format ?? formatNumber;
  const height = props.height ?? DEFAULT_HEIGHT;
  const [asTable, setAsTable] = useState(false);
  // a pizza com tabela já É a tabela; o botão seria um segundo caminho para o mesmo lugar
  const canToggle =
    props.kind !== 'pizza-tabela' &&
    !props.withoutTable &&
    data.length > 0 &&
    !error &&
    !loading;

  const header = (
    <div className="flex items-center gap-2">
      {actions}
      {canToggle && (
        <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
          <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
          {asTable ? 'Ver gráfico' : 'Ver tabela'}
        </Button>
      )}
      {filters && (
        <Filters
          fields={filters.fields}
          values={filters.values}
          onApply={filters.onApply}
        />
      )}
    </div>
  );

  let body: ReactNode;
  if (error) {
    body = (
      <Alert tone="erro" title="Não conseguimos carregar este gráfico">
        Nada foi desenhado, para não mostrar número velho como se fosse atual.
        <span className="mt-1 block font-mono text-xs">{error.message}</span>
      </Alert>
    );
  } else if (loading) {
    body = <ChartSkeleton kind={props.kind} height={height} label={label} />;
  } else if (data.length === 0) {
    body = (
      <p
        className="grid place-items-center px-4 text-center text-sm text-texto-suave"
        style={{ height }}
      >
        {empty}
      </p>
    );
  } else {
    body = <ChartBody {...props} format={format} height={height} asTable={asTable} />;
  }

  if (props.bare) {
    return (
      <div role="group" aria-label={label} className="flex flex-col gap-2">
        {(canToggle || filters || actions) && (
          <div className="flex justify-end">{header}</div>
        )}
        {body}
      </div>
    );
  }

  return (
    <Card title={title} descricao={descricao} actions={header}>
      <div role="group" aria-label={label}>
        {body}
      </div>
    </Card>
  );
}

/* As alturas das colunas do esqueleto, em % — fixas, para a espera não piscar outra figura a cada render. */
const SKELETON_BARS = [42, 66, 54, 80, 61, 38, 72, 50, 86, 58, 70, 47] as const;

/**
 * A espera com a forma do que vem: colunas que pulsam em onda da esquerda
 * para a direita, ou o anel da pizza. Um retângulo cinza dizia "carregando"
 * sem dizer o quê, e a figura de verdade nascia de outro tamanho.
 */
function ChartSkeleton({
  kind,
  height,
  label,
}: {
  kind: ChartProps<unknown>['kind'];
  height: number;
  label: string;
}) {
  const pie = kind === 'pizza' || kind === 'pizza-tabela';
  return (
    <div
      className="grid place-items-center"
      style={{ height }}
      aria-busy="true"
      aria-label={`Carregando: ${label}`}
    >
      {pie ? (
        <div
          className="aspect-square h-[85%] animate-pulse rounded-full border-[1.75rem] border-borda"
          aria-hidden
        />
      ) : (
        <div className="flex size-full items-end gap-2 px-2 pb-6 pl-16" aria-hidden>
          {SKELETON_BARS.map((h, i) => (
            <span
              key={i}
              className="flex-1 animate-pulse rounded-t-sm bg-borda"
              style={{ height: `${h}%`, animationDelay: `${i * 90}ms` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChartBody<T>(
  props: ChartProps<T> & {
    format: (v: number) => string;
    height: number;
    asTable: boolean;
  },
) {
  switch (props.kind) {
    case 'linha':
    case 'barra':
      return (
        <Cartesian
          {...props}
          axisFormat={props.axisFormat ?? props.format}
          area={props.kind === 'linha' && props.area === true}
          series={props.series.map((s) => ({
            ...s,
            mark: props.kind === 'linha' ? 'linha' : 'barra',
          }))}
        />
      );
    case 'barra-linha':
      return <Cartesian {...props} axisFormat={props.axisFormat ?? props.format} />;
    case 'pizza':
    case 'pizza-tabela':
      return <PieBody {...props} />;
    case 'corrida':
      return <Race {...props} />;
  }
}

/* ── linha, barra e as duas juntas ───────────────────────────────────────── */

/**
 * A chave de cada série dentro da linha do Recharts. O prefixo impede uma série
 * chamada `category` de apagar a categoria.
 */
const dataKeyOf = (key: string) => `s_${key}`;

function Cartesian<T>({
  label,
  data,
  category,
  series,
  stacked = false,
  area = false,
  tooltip,
  format,
  axisFormat,
  height,
  asTable,
  onSelect,
  selected,
}: {
  label: string;
  data: readonly T[];
  category: (item: T) => string;
  series: (SeriesSpec<T> & { mark: 'barra' | 'linha' })[];
  stacked?: boolean;
  area?: boolean;
  tooltip?: (point: ChartPoint<T>) => ReactNode;
  format: (v: number) => string;
  axisFormat: (v: number) => string;
  height: number;
  asTable: boolean;
  onSelect?: (item: T) => void;
  selected?: (item: T) => boolean;
}) {
  const rows = useMemo(
    () => cartesianRows(data, category, series),
    [data, category, series],
  );
  const palette = seriesColors(series);
  const colored = series.map((s, i) => ({ ...s, color: palette[i]! }));
  const reduced = useReducedMotion();
  // o `id` do degradê da área: dois gráficos na mesma tela não dividem um
  const gradientId = `area${useId().replace(/:/g, '')}`;

  if (asTable) {
    return (
      <Table
        label={label}
        empty=""
        paginate={false}
        items={rows.map((r, index) => ({ ...r, index }))}
        rowKey={(r) => String(r.index)}
        columns={[
          { title: 'Categoria', cell: (r) => r.category },
          ...colored.map((s): Column<(typeof rows)[number]> => ({
            title: s.label,
            alignment: 'fim',
            cell: (r) => valueText(r.values[s.key] ?? null, format),
          })),
        ]}
      />
    );
  }

  const flat = rows.map((r, index) => ({
    __category: r.category,
    __index: index,
    ...Object.fromEntries(colored.map((s) => [dataKeyOf(s.key), r.values[s.key]])),
  }));

  const chosen = selected ? rows.findIndex((r) => selected(r.item)) : -1;
  const pointOf = (index: number): ChartPoint<T> | null => {
    const row = rows[index];
    if (!row) return null;
    return {
      category: row.category,
      item: row.item,
      values: colored.map((s) => ({
        key: s.key,
        label: s.label,
        value: row.values[s.key] ?? null,
        color: s.color,
      })),
    };
  };

  return (
    <>
      <ChartContainer config={{}} className="aspect-auto w-full" style={{ height }}>
        <ComposedChart
          data={flat}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          style={onSelect ? { cursor: 'pointer' } : undefined}
          onClick={
            onSelect
              ? (state: { activeTooltipIndex?: number | string | null }) => {
                  const row = rows[Number(state?.activeTooltipIndex)];
                  if (row) onSelect(row.item);
                }
              : undefined
          }
        >
          {area && (
            <defs>
              {colored.map((s) => (
                <linearGradient
                  key={s.key}
                  id={`${gradientId}-${s.key}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={colorVar(s.color)} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={colorVar(s.color)} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
          )}
          <CartesianGrid
            vertical={false}
            stroke="var(--cor-borda-suave)"
            strokeDasharray={area ? '4 4' : undefined}
          />
          <XAxis
            dataKey="__category"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={16}
            // só linha: sem barra a escala é de pontos, e o primeiro e o último
            // rótulo ficavam meio para fora da caixa ("set/2…"), medido em 28/09
            padding={
              colored.some((s) => s.mark === 'barra')
                ? undefined
                : { left: 24, right: 24 }
            }
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            width={64}
            tickFormatter={(v: number) => axisFormat(v)}
          />
          <ChartTooltip
            cursor={{ fill: 'var(--cor-superficie-suave)', stroke: 'var(--cor-borda)' }}
            content={({ active, payload }) => {
              const index = payload?.[0]?.payload?.__index;
              const point = active && typeof index === 'number' ? pointOf(index) : null;
              if (!point) return null;
              return (
                <TooltipShell>
                  {tooltip ? (
                    tooltip(point)
                  ) : (
                    <DefaultPointTooltip point={point} format={format} />
                  )}
                </TooltipShell>
              );
            }}
          />
          {colored.map((s) =>
            s.mark === 'barra' ? (
              <Bar
                key={s.key}
                dataKey={dataKeyOf(s.key)}
                name={s.label}
                fill={colorVar(s.color)}
                stackId={stacked ? 'pilha' : undefined}
                radius={stacked ? 0 : [4, 4, 0, 0]}
                maxBarSize={32}
                isAnimationActive={!reduced}
                animationDuration={800}
                animationEasing="ease-out"
              >
                {/* com uma escolha, as outras colunas apagam — a escolhida fica cheia */}
                {selected &&
                  rows.map((r, index) => (
                    <Cell
                      key={index}
                      fill={colorVar(s.color)}
                      fillOpacity={chosen === -1 || chosen === index ? 1 : 0.3}
                    />
                  ))}
              </Bar>
            ) : area ? (
              <Area
                key={s.key}
                dataKey={dataKeyOf(s.key)}
                name={s.label}
                type="monotone"
                stroke={colorVar(s.color)}
                strokeWidth={2}
                fill={`url(#${gradientId}-${s.key})`}
                dot={false}
                activeDot={{ r: 5, stroke: 'var(--cor-superficie)', strokeWidth: 2 }}
                connectNulls={false}
                isAnimationActive={!reduced}
              />
            ) : (
              <Line
                key={s.key}
                dataKey={dataKeyOf(s.key)}
                name={s.label}
                type="monotone"
                stroke={colorVar(s.color)}
                strokeWidth={2}
                dot={{ r: 3.5, fill: colorVar(s.color), strokeWidth: 0 }}
                activeDot={{ r: 5, stroke: 'var(--cor-superficie)', strokeWidth: 2 }}
                // o mês sem medida é um buraco na linha, e não uma ponte inventada
                connectNulls={false}
                isAnimationActive={!reduced}
              />
            ),
          )}
        </ComposedChart>
      </ChartContainer>
      {/* uma série só não tem legenda: o título já diz o que ela é */}
      {colored.length > 1 && (
        <Legend
          items={colored.map((s) => ({ label: s.label, color: s.color, mark: s.mark }))}
        />
      )}
    </>
  );
}

function DefaultPointTooltip<T>({
  point,
  format,
}: {
  point: ChartPoint<T>;
  format: (v: number) => string;
}) {
  return (
    <>
      <p className="font-semibold text-texto">{point.category}</p>
      <ul className="mt-1.5 grid gap-1">
        {point.values.map((v) => (
          <li key={v.key} className="flex items-center gap-2">
            <Swatch color={v.color} />
            <span className="text-texto-suave">{v.label}</span>
            <span className="ml-auto pl-3 font-medium text-texto tabular-nums">
              {valueText(v.value, format)}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

/* ── pizza ────────────────────────────────────────────────────────────────── */

function PieBody<T>({
  kind,
  label,
  data,
  slice,
  maxSlices,
  columns = [],
  tooltip,
  center,
  onSelect,
  format,
  height,
  asTable,
}: PieProps<T> & {
  label: string;
  data: readonly T[];
  format: (v: number) => string;
  height: number;
  asTable: boolean;
}) {
  const result = useMemo(
    () => pieSlices(data, slice.label, slice.value, maxSlices),
    [data, slice, maxSlices],
  );
  const reduced = useReducedMotion();

  if (result.kind === 'negativo') {
    return (
      <Alert tone="alerta" title="A pizza não desenha valor negativo">
        Não existe fatia de área negativa, e tirar essas linhas mudaria a porcentagem de
        todas as outras. Negativos: {result.labels.join(', ')}.
      </Alert>
    );
  }
  if (result.kind === 'vazio') {
    return (
      <p
        className="grid place-items-center px-4 text-center text-sm text-texto-suave"
        style={{ height }}
      >
        Todos os valores são zero — não há parte de um todo para desenhar.
      </p>
    );
  }

  const slices = result.slices.map((s) => {
    const own = s.others ? undefined : slice.color?.(s.items[0] as T);
    return own === undefined ? s : { ...s, color: own };
  });
  const select = (s: Slice<T> | undefined) => {
    if (onSelect && s && !s.others) onSelect(s.items[0] as T);
  };
  // cada registro na tabela, com a cor da fatia em que ele caiu
  const colorOfItem = new Map<T, ChartColor>();
  for (const s of slices) for (const item of s.items) colorOfItem.set(item, s.color);
  const shareOfItem = itemShares(slices, slice.value);

  const table = (
    <Table
      label={label}
      empty=""
      paginate={false}
      items={[...data]
        .map((item, index) => ({ item, index }))
        .sort((a, b) => slice.value(b.item) - slice.value(a.item))}
      rowKey={(r) => String(r.index)}
      columns={[
        {
          title: 'Fatia',
          cell: (r) => (
            <span className="flex items-center gap-2">
              <Swatch color={colorOfItem.get(r.item) ?? 'fase-cinza'} />
              {onSelect ? (
                <button
                  type="button"
                  className="text-left font-medium text-texto underline-offset-2 hover:underline active:text-texto-suave"
                  onClick={() => onSelect(r.item)}
                >
                  {slice.label(r.item)}
                </button>
              ) : (
                slice.label(r.item)
              )}
            </span>
          ),
        },
        { title: 'Valor', alignment: 'fim', cell: (r) => format(slice.value(r.item)) },
        {
          title: 'Participação',
          alignment: 'fim',
          cell: (r) => {
            // o registro zero não é fatia: não tem participação para mostrar
            const share = shareOfItem.get(r.item);
            return share === undefined ? '—' : formatShare(share);
          },
        },
        ...columns.map(
          ({ cell, sortValue, ...c }): Column<{ item: T; index: number }> => ({
            ...c,
            cell: (r) => cell(r.item),
            ...(sortValue && { sortValue: (r: { item: T }) => sortValue(r.item) }),
          }),
        ),
      ]}
    />
  );

  if (asTable) return table;

  const pie = (
    <div className="relative">
      <ChartContainer config={{}} className="aspect-auto w-full" style={{ height }}>
        <PieChart>
          <ChartTooltip
            content={({ active, payload }) => {
              const index = payload?.[0]?.payload?.__index;
              const s = active && typeof index === 'number' ? slices[index] : undefined;
              if (!s) return null;
              return (
                <TooltipShell>
                  {tooltip ? (
                    tooltip(s)
                  ) : (
                    <>
                      <p className="flex items-center gap-2 font-semibold text-texto">
                        <Swatch color={s.color} />
                        {s.label}
                      </p>
                      <p className="mt-1 text-texto-suave tabular-nums">
                        {format(s.value)} · {formatShare(s.shareTenths)}
                      </p>
                      {s.others && (
                        <p className="mt-1 text-texto-tenue">
                          {s.items.length} registros somados
                        </p>
                      )}
                    </>
                  )}
                </TooltipShell>
              );
            }}
          />
          <Pie
            data={slices.map((s, index) => ({
              __index: index,
              label: s.label,
              value: s.value,
              fill: colorVar(s.color),
            }))}
            dataKey="value"
            nameKey="label"
            outerRadius="90%"
            innerRadius={center === undefined ? 0 : '60%'}
            // a primeira fatia (a maior) começa ao meio-dia e segue no sentido do relógio
            startAngle={90}
            endAngle={-270}
            // o respiro de 2px entre fatias, na cor do cartão
            stroke="var(--cor-superficie)"
            strokeWidth={2}
            isAnimationActive={!reduced}
            animationDuration={900}
            animationEasing="ease-out"
            {...(onSelect && {
              className: 'cursor-pointer',
              onClick: (_: unknown, index: number) => select(slices[index]),
            })}
          />
        </PieChart>
      </ChartContainer>
      {center !== undefined && (
        // o miolo não pega o ponteiro: a dica continua respondendo no anel
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div>{center}</div>
        </div>
      )}
    </div>
  );

  if (kind === 'pizza-tabela') {
    return (
      // a pizza é a leitura e a tabela é o apoio: ela fica com a parte maior
      <div className="grid items-center gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {pie}
        {table}
      </div>
    );
  }
  return (
    <>
      {pie}
      <Legend
        items={slices.map((s) => ({
          label: `${s.label} · ${formatShare(s.shareTenths)}`,
          color: s.color,
          mark: 'barra' as const,
        }))}
      />
    </>
  );
}

/* ── corrida ──────────────────────────────────────────────────────────────── */

function Race<T>({
  label,
  data,
  frame,
  entity,
  value,
  top = 8,
  interval = 1200,
  format,
  height,
  asTable,
}: RaceProps<T> & {
  label: string;
  data: readonly T[];
  format: (v: number) => string;
  height: number;
  asTable: boolean;
}) {
  const frames = useMemo(
    () => raceFrames(data, frame, entity, value, top),
    [data, frame, entity, value, top],
  );
  const colors = useMemo(() => raceColors(frames), [frames]);
  // o eixo é o mesmo em todos os quadros: reescalar a cada quadro apagaria o crescimento
  const max = useMemo(
    () => Math.max(0, ...frames.flatMap((f) => f.bars.map((b) => b.value))),
    [frames],
  );
  const reduced = useReducedMotion();
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const last = frames.length - 1;
  const current = frames[Math.min(position, last)];

  // novos dados voltam ao começo, em vez de apontar para um quadro que não existe mais
  useEffect(() => {
    setPosition(0);
    setPlaying(false);
  }, [frames]);

  useEffect(() => {
    if (!playing) return;
    if (position >= last) {
      setPlaying(false);
      return;
    }
    const t = window.setTimeout(() => setPosition((p) => p + 1), interval);
    return () => window.clearTimeout(t);
  }, [playing, position, last, interval]);

  if (!current) return null;

  if (asTable) {
    return (
      <Table
        label={`${label} — ${current.frame}`}
        empty=""
        paginate={false}
        items={current.bars}
        rowKey={(b) => b.entity}
        columns={[
          { title: 'Posição', width: '5rem', cell: (b) => `${b.rank}º` },
          { title: 'Nome', cell: (b) => b.entity },
          { title: 'Valor', alignment: 'fim', cell: (b) => format(b.value) },
        ]}
      />
    );
  }

  return (
    <>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-lg font-bold text-texto tabular-nums" aria-live="polite">
          {current.frame}
        </p>
        <p className="text-xs text-texto-suave">
          Quadro {Math.min(position, last) + 1} de {frames.length}
        </p>
      </div>
      <ChartContainer config={{}} className="aspect-auto w-full" style={{ height }}>
        <BarChart
          layout="vertical"
          data={current.bars.map((b) => ({
            ...b,
            fill: colorVar(colors.get(b.entity) ?? 'fase-laranja'),
          }))}
          margin={{ top: 4, right: 72, bottom: 0, left: 0 }}
        >
          <XAxis type="number" domain={[0, max]} hide />
          <YAxis
            type="category"
            dataKey="entity"
            width={128}
            tickLine={false}
            axisLine={false}
          />
          <Bar
            dataKey="value"
            radius={[0, 4, 4, 0]}
            maxBarSize={28}
            isAnimationActive={!reduced}
            animationDuration={Math.min(interval * 0.8, 800)}
          >
            {/* o número escrito na ponta: com mais de seis nomes a cor é uma só, e quem identifica é o texto */}
            <LabelList
              dataKey="value"
              position="right"
              className="fill-texto"
              formatter={(v: unknown) => (typeof v === 'number' ? format(v) : '')}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
      <div className="mt-2 flex items-center gap-2">
        <Button
          variant="discreto"
          size="sm"
          aria-label="Quadro anterior"
          disabled={position <= 0}
          onClick={() => {
            setPlaying(false);
            setPosition((p) => Math.max(0, p - 1));
          }}
        >
          <Icon nome="seta-esquerda" size={16} />
        </Button>
        <Button
          variant="contorno"
          size="sm"
          disabled={last === 0}
          onClick={() => {
            // tocar do fim recomeça, em vez de não fazer nada
            if (!playing && position >= last) setPosition(0);
            setPlaying((p) => !p);
          }}
        >
          <Icon nome={playing ? 'pausa' : 'tocar'} size={16} />
          {playing ? 'Pausar' : 'Tocar'}
        </Button>
        <Button
          variant="discreto"
          size="sm"
          aria-label="Próximo quadro"
          disabled={position >= last}
          onClick={() => {
            setPlaying(false);
            setPosition((p) => Math.min(last, p + 1));
          }}
        >
          <Icon nome="seta-direita" size={16} />
        </Button>
        <label className="ml-2 flex min-w-0 flex-1 items-center gap-2 text-xs text-texto-suave">
          Quadro
          <input
            type="range"
            min={0}
            max={last}
            value={Math.min(position, last)}
            disabled={last === 0}
            onChange={(e) => {
              setPlaying(false);
              setPosition(Number(e.target.value));
            }}
            aria-valuetext={current.frame}
            className="min-w-0 flex-1 cursor-pointer accent-primaria-texto hover:accent-primaria disabled:cursor-not-allowed disabled:opacity-50"
          />
        </label>
      </div>
    </>
  );
}

/* ── peças ────────────────────────────────────────────────────────────────── */

/** A casca da dica — a mesma do cartão flutuante, com o conteúdo de quem chama dentro. */
function TooltipShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-w-40 max-w-72 rounded-borda border border-borda bg-superficie px-3 py-2 text-xs shadow-flutuante">
      {children}
    </div>
  );
}

function Swatch({
  color,
  mark = 'barra',
}: {
  color: ChartColor;
  mark?: 'barra' | 'linha';
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'shrink-0',
        mark === 'linha' ? 'h-0.5 w-3 rounded-full' : 'size-2.5 rounded-[2px]',
      )}
      style={{ backgroundColor: colorVar(color) }}
    />
  );
}

/** A legenda: o nome em cor de texto, e só a marca ao lado leva a cor da série. */
function Legend({
  items,
}: {
  items: { label: string; color: ChartColor; mark: 'barra' | 'linha' }[];
}) {
  return (
    <ul className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-texto-suave">
      {items.map((i) => (
        <li key={i.label} className="flex items-center gap-1.5">
          <Swatch color={i.color} mark={i.mark} />
          {i.label}
        </li>
      ))}
    </ul>
  );
}

function valueText(value: number | null, format: (v: number) => string): string {
  return value === null ? 'sem medida' : format(value);
}

/** `prefers-reduced-motion`: sem animação de entrada nem de troca de quadro. */
function useReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setReduced(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return reduced;
}

export type { ChartColor, SeriesSpec, Slice } from './chart.js';
export { MAX_SERIES, SERIES_COLORS, formatShare } from './chart.js';
